import urlParser from 'js-video-url-parser';

const VIMEO_REGEX = /^https?:\/\/(www\.)?(vimeo\.com\/(video\/)?[0-9]+|player\.vimeo\.com\/video\/[0-9]+)(\?.*)?$/;
const YOUTUBE_REGEX = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]{11}(?:[?&].*)?$/;

export const isVimeoUrl = (url: string) => {
  return VIMEO_REGEX.test(url);
};

export const isYoutubeUrl = (url: string) => {
  return YOUTUBE_REGEX.test(url);
};

export const isVideoUrl = (url: string) => {
  return isVimeoUrl(url) || isYoutubeUrl(url);
};

export const extractVimeoVideoId = (videoUrl: string): string | null => {
  const vimeoMatch = videoUrl.match(/(?:player\.)?vimeo\.com\/(?:video\/)?([0-9]+)/);

  if (vimeoMatch) {
    return vimeoMatch[1];
  }

  return null;
};

export const extractYoutubeVideoId = (url: string): string | null => {
  const youtubeMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})(?:[?&]|$)/);

  if (youtubeMatch) {
    return youtubeMatch[1];
  }

  const youtubeShortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})(?:[?&]|$)/);
  if (youtubeShortMatch) {
    return youtubeShortMatch[1];
  }

  return null;
};

// Shared Vimeo background-video param set used by the guest VideoBackgroundRenderer and the
// admin template thumbnail's raw-iframe path. Keeping it here means changes to the embed-URL
// shape (e.g. adding `quality=720p`) update both call sites at once.
const VIMEO_BACKGROUND_PARAMS = [
  ['background', '1'],
  ['autoplay', '1'],
  ['loop', '1'],
  ['muted', '1'],
  ['playsinline', '1'],
] as const;

/**
 * Returns a player.vimeo.com embed URL configured for background playback (no chrome, looping,
 * muted, autoplay). Preserves any caller-provided params already on the input URL. Returns
 * `null` when the input isn't recognisable as a Vimeo URL.
 */
export const buildVimeoBackgroundEmbedUrl = (videoUrl: string): string | null => {
  if (!isVimeoUrl(videoUrl)) return null;
  const videoId = extractVimeoVideoId(videoUrl);
  if (!videoId) return null;
  let searchParams: URLSearchParams;
  try {
    searchParams = new URL(videoUrl).searchParams;
  } catch {
    return null;
  }
  VIMEO_BACKGROUND_PARAMS.forEach(([key, value]) => {
    if (!searchParams.has(key)) searchParams.set(key, value);
  });
  return `https://player.vimeo.com/video/${videoId}?${searchParams.toString()}`;
};

/**
 * Returns a youtube.com/embed URL configured for background playback (no controls/branding,
 * looping via `playlist=<videoId>`, muted, autoplay). `enablejsapi=1` is included so callers
 * can drive playback via postMessage (`{event: 'command', func: 'playVideo'|'pauseVideo'}`).
 * For richer recovery (paused-state retry, tab-visibility wake-up), use the react-youtube
 * based YoutubeVideoContainer instead — this helper is for lightweight raw-iframe contexts.
 */
export const buildYoutubeBackgroundEmbedUrl = (videoId: string): string =>
  `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&playsinline=1&enablejsapi=1`;

export const getVideoDataFromUrl = (url: string) => {
  const videoInfo = urlParser.parse(url);

  if (!videoInfo) {
    return;
  }

  const embedUrl = urlParser.create({
    videoInfo,
    format: 'embed',
  });
  const thumbnailUrl =
    videoInfo.provider === 'youtube'
      ? urlParser.create({
          videoInfo,
          params: {
            imageQuality: 'maxresdefault',
          },
          format: 'longImage',
        })
      : `https://vumbnail.com/${videoInfo.id}.jpg`;

  return {
    embedUrl,
    thumbnailUrl,
  };
};
